import {
  ChatIcon,
  CoachProfile,
  FlexBox,
  GearIcon,
  JoonUIColor,
  SPACING,
  TextButton,
  Typography,
  VideoIcon,
  WarningTriangleIcon,
} from "@joonapp/web-shared"
import { useId, useState } from "react"
import { Tooltip } from "react-tooltip"

import useAdminPageStore from "./hooks/useAdminpageStore"
import useSubscriptionTypes from "./hooks/useSubscriptionTypes"
import ProfileImage from "../../components/profileImage/ProfileImage"
import useUserRole from "../../hooks/useUserRole"
import {
  useCoachingGroupsQuery,
  useCoachSubscriptions,
} from "../../networking/coachingGroups/queries"

const CoachListItem = ({ profile }: { profile: CoachProfile }) => {
  const [isHovered, setIsHovered] = useState(false)
  const {
    selectedCoachProfileId,
    setSelectedCoachProfileId,
    setSearchValue,
    setDebouncedSearchValue,
  } = useAdminPageStore()

  const tooltipId = useId()

  const { data: coachingGroups } = useCoachingGroupsQuery()
  const { isAdmin } = useUserRole()

  const coachChannels =
    coachingGroups?.filter((group) =>
      group.profiles.some((p) => Number(p.id) === profile.id)
    ) || []

  const isSelected = selectedCoachProfileId === profile.id

  const { data: coachingSubscriptions } = useCoachSubscriptions(profile.user.id)
  const { numVideoSubs, numChatSubs } = useSubscriptionTypes(
    coachingSubscriptions || []
  )
  const totalSlots = profile.max_available_slots
  const numAvailableSlots = totalSlots - coachChannels?.length
  const numFilledSlots = totalSlots - numAvailableSlots
  const numSlotsDisplayed = profile.num_open_slots

  const showWarning = numFilledSlots === 0 || numAvailableSlots === 0

  const navigateToCoachAdminPage = () => {
    window.open(
      `https://app.joonapp.io/admin/accounts/profile/${profile.id}/change`,
      "_blank"
    )
  }

  const handleSelectCoach = () => {
    setSelectedCoachProfileId(profile.id)
    setSearchValue("")
    setDebouncedSearchValue("")
  }

  return (
    <button
      onClick={handleSelectCoach}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ width: "100%" }}
    >
      <FlexBox
        direction="row"
        gap={SPACING.space2}
        wrap={false}
        align="center"
        style={{
          padding: SPACING.space3,
          width: "100%",
          borderBottom: `1px solid ${JoonUIColor.border.default}`,
          backgroundColor: isSelected
            ? JoonUIColor.background.lightGray
            : isHovered
            ? JoonUIColor.background.xlightGray
            : "transparent",
          borderRight: isSelected
            ? `4px solid ${JoonUIColor.border.accent}`
            : "none",
        }}
      >
        <ProfileImage
          profileUrl={profile.profile_image_url ?? undefined}
          size="small"
          userName={profile.user.name}
        />
        <Typography variant="body" textAlign="left">
          {profile.user.name} {profile.user.last_name}
        </Typography>
        <FlexBox
          direction="row"
          gap={SPACING.space3}
          wrap={false}
          align="center"
          style={{ width: "fit-content", whiteSpace: "nowrap" }}
        >
          <FlexBox
            direction="row"
            gap={SPACING.space2}
            align="center"
            wrap={false}
            data-tooltip-id={tooltipId}
          >
            {showWarning && (
              <div style={{ minWidth: "12px" }}>
                <WarningTriangleIcon
                  size={12}
                  color={JoonUIColor.semantic.warning}
                />
              </div>
            )}
            <div>
              <Typography variant="bodySmall">{numFilledSlots} </Typography>
              <Typography
                variant="bodySmall"
                color={JoonUIColor.text.secondary}
              >
                / {totalSlots}{" "}
              </Typography>
              <Typography
                variant="bodySmall"
                color={JoonUIColor.text.secondary}
              >
                ({numSlotsDisplayed})
              </Typography>
            </div>

            <Tooltip id={tooltipId} place="top">
              {showWarning && (
                <Typography
                  variant="bodySmall"
                  color={JoonUIColor.text.inverted}
                >
                  {numAvailableSlots === 0
                    ? `Currently at capacity!`
                    : "No clients yet!"}
                </Typography>
              )}
              <FlexBox
                direction="row"
                gap={SPACING.space1}
                align="center"
                wrap={false}
              >
                <VideoIcon size={14} color={JoonUIColor.icon.inverted} />
                <Typography
                  variant="bodySmallBold"
                  color={JoonUIColor.text.inverted}
                >
                  {numVideoSubs} session
                </Typography>
              </FlexBox>
              <FlexBox
                direction="row"
                gap={SPACING.space1}
                align="center"
                wrap={false}
              >
                <ChatIcon size={12} color={JoonUIColor.icon.inverted} />
                <Typography
                  variant="bodySmallBold"
                  color={JoonUIColor.text.inverted}
                >
                  {numChatSubs} message
                </Typography>
              </FlexBox>
            </Tooltip>
          </FlexBox>
          {isAdmin && (
            <TextButton onClick={navigateToCoachAdminPage}>
              <GearIcon size={16} color={JoonUIColor.icon.neutral} />
            </TextButton>
          )}
        </FlexBox>
      </FlexBox>
    </button>
  )
}

export default CoachListItem
